import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { LegalAiGuestAccessVo } from '@/types/api'

const AI_GUEST_STORAGE_KEY = 'legal-web:ai-guest-access'
export const AI_GUEST_CONVERSATION_LIMIT_MESSAGE = '游客会话数量已达上限，请登录后继续'

interface AiGuestAccessState {
  guestToken: string | null
  expiresAt: string | null
  maxRounds: string | null
  maxConversations: number | null
  maxInputChars: number | null
  capabilities: string[]
  pendingClaimConversationId: string | null
  setAccess: (access: LegalAiGuestAccessVo) => void
  setPendingClaimConversationId: (conversationId: string | null) => void
  clearAccess: () => void
}

function parseExpiresAt(value: string | null): number | null {
  if (!value) return null
  const timestamp = Date.parse(value.replace(' ', 'T'))
  return Number.isFinite(timestamp) ? timestamp : null
}

export const useAiGuestAccessStore = create<AiGuestAccessState>()(
  persist(
    (set) => ({
      guestToken: null,
      expiresAt: null,
      maxRounds: null,
      maxConversations: null,
      maxInputChars: null,
      capabilities: [],
      pendingClaimConversationId: null,
      setAccess: (access) =>
        set({
          guestToken: access.guestToken?.trim() || null,
          expiresAt: access.expiresAt ?? null,
          maxRounds: access.maxRounds ?? null,
          maxConversations: access.maxConversations ?? null,
          maxInputChars: access.maxInputChars ?? null,
          capabilities: access.capabilities ?? [],
        }),
      setPendingClaimConversationId: (conversationId) =>
        set({ pendingClaimConversationId: conversationId?.trim() || null }),
      clearAccess: () =>
        set({
          guestToken: null,
          expiresAt: null,
          maxRounds: null,
          maxConversations: null,
          maxInputChars: null,
          capabilities: [],
          pendingClaimConversationId: null,
        }),
    }),
    {
      name: AI_GUEST_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        guestToken: state.guestToken,
        expiresAt: state.expiresAt,
        maxRounds: state.maxRounds,
        maxConversations: state.maxConversations,
        maxInputChars: state.maxInputChars,
        capabilities: state.capabilities,
        pendingClaimConversationId: state.pendingClaimConversationId,
      }),
    },
  ),
)

export function getValidAiGuestToken(): string | null {
  const state = useAiGuestAccessStore.getState()
  const token = state.guestToken?.trim()
  if (!token) return null

  const expiresAt = parseExpiresAt(state.expiresAt)
  if (expiresAt !== null && expiresAt <= Date.now()) {
    state.clearAccess()
    return null
  }
  return token
}

export function hasValidAiGuestAccess(): boolean {
  return getValidAiGuestToken() !== null
}

export function isAiGuestConversationLimitMessage(message: string | undefined): boolean {
  return Boolean(message?.includes('游客会话数量已达上限'))
}
